import { ReactElement, ReactNode, useEffect } from 'react'
import produce from 'immer'
import cloneDeep from 'lodash-es/cloneDeep'
import { Object3D } from 'three'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { ImmutableLine3 } from '@modugen/ui-toolkit'
import { usePersistentParams } from 'src/hooks'
import { completeTapelineInput } from './utils'

export type TapelineLineStyle = 'solid' | 'dashed' | 'dashed-long-gap'

export type PlanType = 'PositionPlan' | 'StiffeningPlan' | 'LoadPlan'

export interface PlanTapelineConfig {
  storeysForPlans: string[]
  planTypes: PlanType[]
}

export interface TapelineStyle {
  color: string
  thickness: number
  lineStyle: TapelineLineStyle
}

export interface TapelineText {
  start?: string
  end?: string
  center?: string
}

export type Tapeline = ImmutableLine3 & {
  id: string
  filterViewId: string
  text?: TapelineText
  style: TapelineStyle
  showLengthLabel: boolean
  planConfig: PlanTapelineConfig
}

export type TapelineInput = ImmutableLine3 & {
  id?: string
  filterViewId?: string
  text?: TapelineText
  style?: Partial<TapelineStyle>
  showLengthLabel?: boolean
  planConfig?: PlanTapelineConfig
}

interface TapelineState {
  isVisible: boolean
  isActive: boolean
  isDrawing: boolean
  additionalSnapTargets?: Object3D[]
  lines: Tapeline[]
  selectedId: string | null
  showIndicators: boolean
  indicatorType: 'sphere' | 'crosshair'
  activeFilterViewId: string | null
}

const initialState: TapelineState = {
  isVisible: false,
  isActive: false,
  isDrawing: false,
  showIndicators: true,
  lines: [],
  selectedId: null,
  indicatorType: 'sphere',
  activeFilterViewId: null,
}

const useTapelineStore = create(
  combine(cloneDeep(initialState), set => ({
    clear: () => set(cloneDeep(initialState)),
    setIsActive: (isActive: boolean) => set({ isActive }),
    setIsVisible: (isVisible: boolean) => set({ isVisible }),
    setIsDrawing: (isDrawing: boolean) => set({ isDrawing }),
    setShowIndicators: (showIndicators: boolean) => set({ showIndicators }),
    setIndicatorType: (indicatorType: 'sphere' | 'crosshair') => set({ indicatorType }),
    setAdditionalSnapTargets: (additionalSnapTargets?: Object3D[]) =>
      set({ additionalSnapTargets }),

    addLine: (line: TapelineInput) =>
      set(
        produce((state: TapelineState) => {
          if (!state.activeFilterViewId) return
          state.lines.push(completeTapelineInput(line, state.activeFilterViewId))
        }),
      ),

    updateLine: (lineId: string, update: TapelineInput) =>
      set(
        produce((state: TapelineState) => {
          if (!state.activeFilterViewId) return
          const index = state.lines.findIndex(line => line.id === lineId)
          if (index === -1) return
          const current = state.lines[index]
          state.lines[index] = {
            ...completeTapelineInput(update, state.activeFilterViewId),
            id: current.id,
            filterViewId: current.filterViewId,
          }
        }),
      ),

    updateLineAttributes: (
      lineId: string,
      attributes: Partial<
        Pick<Tapeline, 'showLengthLabel' | 'text' | 'style' | 'start' | 'end' | 'planConfig'>
      >,
    ) =>
      set(
        produce((state: TapelineState) => {
          const index = state.lines.findIndex(item => item.id === lineId)
          if (index === -1) return
          const line = state.lines[index]
          state.lines[index] = {
            ...line,
            showLengthLabel: attributes.showLengthLabel ?? line.showLengthLabel,
            text: attributes.text ?? line.text,
            start: attributes.start ?? line.start,
            end: attributes.end ?? line.end,
            planConfig: attributes.planConfig ?? line.planConfig,
            style: attributes.style
              ? {
                  ...line.style,
                  ...attributes.style,
                }
              : line.style,
          }
        }),
      ),

    removeLine: (lineId: string) =>
      set((state: TapelineState) => ({
        ...state,
        selectedId: state.selectedId === lineId ? null : state.selectedId,
        lines: state.lines.filter(line => line.id !== lineId),
      })),

    removeAllLines: () => set({ lines: [], isVisible: false, isActive: false }),

    setLines: (lines: Tapeline[]) => set({ lines }),

    selectLine: (lineId: string) =>
      set((state: TapelineState) =>
        state.lines.some(line => line.id === lineId) ? { ...state, selectedId: lineId } : state,
      ),
    deselectLine: () => set({ selectedId: null }),
    setActiveFilterViewId: (activeFilterViewId: string | null) => set({ activeFilterViewId }),
  })),
)

interface TapelineStoreProviderProps {
  children: ReactNode
}

const TapelineStoreProvider = ({ children }: TapelineStoreProviderProps): ReactElement => {
  const clear = useTapelineStore(state => state.clear)
  const { projectId }: { projectId?: string } = usePersistentParams()

  useEffect(() => {
    clear()
  }, [clear, projectId])

  useEffect(() => {
    return () => {
      clear()
    }
  }, [clear])

  return <>{children}</>
}

export { TapelineStoreProvider, useTapelineStore }
